import React, { useState } from "react";

import {
  Box,
  Collapse,
  FormHelperText,
  IconButton,
  Typography,
} from "@mui/material";

import { CaretUpIcon } from "@phosphor-icons/react/dist/ssr";

import { htmlContentStyles, isHtmlContentEmpty } from "@/utils/htmlContentStyle";
import { renderMathInHtml } from "@/utils/mathRenderer";

import { EditingField } from "../Problems/features";

import LessonPlanActionButtons from "./LessonPlanActionButtons";

interface Props {
  index: number;
  step: {
    title: string;
    duration: string;
    content: string;
  };
  onUpdate: (index: number, updatedTitle: string, updatedContent: string) => void;
}

interface ValidationErrors {
  title?: string;
  content?: string;
}

const LessonPlanStep: React.FC<Props> = ({ index, step, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [editedStep, setEditedStep] = useState<typeof step>(step);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!editedStep.title?.trim()) {
      newErrors.title = "Title is required";
      isValid = false;
    }

    if (isHtmlContentEmpty(editedStep.content)) {
      newErrors.content = "Content is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = () => {
    if (validateForm()) {
      onUpdate(index, editedStep.title, editedStep.content);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditedStep(step);
    setErrors({});
    setIsEditing(false);
  };

  const handleChange = (field: keyof typeof step, value: unknown) => {
    setEditedStep((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for the field being changed
    if (errors[field as keyof ValidationErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  return (
    <Box
      sx={{
        width: "100%",
        my: 1.5,
      }}
    >
      <Box
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          py: 1,
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          borderBottom: "1px solid",
          borderColor: "rgba(28, 72, 46, 0.45)",

        }}
      >
        <Typography variant="text13B" flex={1}>
          {index + 1}. {step.title} {step.duration ? `(${step.duration})` : ""}
        </Typography>
        <IconButton size="small" sx={{ color: "text.primary" }}>
          <CaretUpIcon
            size={16}
            weight="bold"
            style={{
              transform: isOpen ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          />
        </IconButton>
      </Box>
      <Collapse in={isOpen} sx={{ width: "100%" }}>
        <Box sx={{
          pt: 2,
          pb: 1,
          px: 1.5
        }}>
          {isEditing ? (
            <>
              <Box sx={{ mb: 2 }}>
                <EditingField
                  label="Title"
                  value={editedStep.title}
                  onChange={(value) => handleChange("title", value)}
                  placeholder="Enter step title"
                  isEditing={isEditing}
                  error={!!errors.title}
                />
                {errors.title && <FormHelperText error>{errors.title}</FormHelperText>}
              </Box>
              <Box sx={{ mb: 2 }}>
                <EditingField
                  initialHeight={600}
                  label="Content"
                  value={editedStep.content}
                  onChange={(value) => handleChange("content", value)}
                  type="textarea"
                  placeholder="Enter the content"
                  isEditing={isEditing}
                  error={!!errors.content}
                />
                {errors.content && (
                  <FormHelperText error>{errors.content}</FormHelperText>
                )}
              </Box>
            </>
          ) : (
            <Typography
              variant="text13L"
              dangerouslySetInnerHTML={{ __html: renderMathInHtml(step.content) }}
              sx={htmlContentStyles}
            />
          )}

          <LessonPlanActionButtons
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default LessonPlanStep;
